import React, {useEffect, useMemo, useState} from "react";
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Radio, RadioGroup, Stack, TextField, Typography} from "@mui/material";
import {getBackendErrorMessage} from "../../../shared/utils/httpError";
import {createLicenca, listEmpresasReference, listPlanosReference} from "../LicencasService";
import {getMeioLabel, novaChaveDeAtivacao, situacaoInicial} from "../assinaturasUtils";

const reais = (valor) => new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(Number(valor) || 0);
const hojeIso = () => new Intl.DateTimeFormat('en-CA', {timeZone: 'America/Sao_Paulo'}).format(new Date());
const itensDe = (response) => response?.data?.valor?.items || [];

const TEXTO_DA_SITUACAO = {
    ATIVA: 'Nasce ativa, com acesso, e entra no próximo fechamento da empresa. No pré-pago, o primeiro período sai numa fatura de entrada.',
    AGUARDANDO_CARTAO: 'Nasce aguardando o cartão: sem acesso e fora do faturamento até a empresa cadastrar o cartão no checkout. O trial começa no cadastro.',
};

/*
 * Assinatura nova (BBF-147): empresa, plano (na versão à venda) e meio aceito pela versão. Sem JSON livre e sem
 * tenant: o backoffice gera o tenant, e os opcionais se contratam na aba Direito e opcionais.
 */
export default function NovaAssinaturaDialog({aberto, empresaInicial = '', onFechar, onCriada}) {
    const [referencias, setReferencias] = useState({empresas: [], planos: [], carregando: true});
    const [form, setForm] = useState({id_empresa: empresaInicial, id_plano: '', meio_pagamento: ''});
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    useEffect(() => {
        if (!aberto) return undefined;
        let ativo = true;
        setForm({id_empresa: empresaInicial, id_plano: '', meio_pagamento: ''});
        setErro('');
        Promise.all([listEmpresasReference(), listPlanosReference()])
            .then(([empresas, planos]) => ativo && setReferencias({
                empresas: itensDe(empresas),
                // Só planos com versão à venda e de sistema que não é de uso interno: assinatura nova entra na versão à venda.
                planos: itensDe(planos).filter((plano) => plano.versao_a_venda && !plano.sistema?.uso_interno && plano.sistema?.status !== 'INTERNO'),
                carregando: false,
            }))
            .catch((error) => ativo && setErro(getBackendErrorMessage(error, 'Não foi possível carregar empresas e planos.')));
        return () => {
            ativo = false;
        };
    }, [aberto, empresaInicial]);

    const plano = useMemo(() => referencias.planos.find((item) => item.id === form.id_plano), [form.id_plano, referencias.planos]);
    const versao = plano?.versao_a_venda;
    const meios = versao?.meios_aceitos || [];
    const status = situacaoInicial(form.meio_pagamento);

    const escolherPlano = (idPlano) => {
        const escolhido = referencias.planos.find((item) => item.id === idPlano);
        const aceitos = escolhido?.versao_a_venda?.meios_aceitos || [];
        setForm((atual) => ({...atual, id_plano: idPlano, meio_pagamento: aceitos.includes('BOLETO') ? 'BOLETO' : aceitos[0] || ''}));
    };

    const criar = async () => {
        setSalvando(true);
        setErro('');
        try {
            const empresa = referencias.empresas.find((item) => item.id === form.id_empresa);
            const response = await createLicenca({
                id_empresa: form.id_empresa,
                id_plano: form.id_plano,
                id_plano_versao: versao.id,
                meio_pagamento: form.meio_pagamento,
                status,
                tipo: 'PAGO',
                validade_inicio: hojeIso(),
                chave_ativacao: novaChaveDeAtivacao(empresa),
            });
            onCriada(response?.data?.data || null);
        } catch (error) {
            setErro(getBackendErrorMessage(error, 'Não foi possível criar a assinatura.'));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={aberto} onClose={salvando ? undefined : onFechar} fullWidth maxWidth="sm">
            <DialogTitle>Nova assinatura</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    <Typography variant="body2" color="text.secondary">
                        A assinatura é o tenant da empresa no sistema, gerado pelo backoffice. Uma empresa pode ter várias, e recebe uma fatura só.
                    </Typography>
                    {erro ? <Alert severity="error">{erro}</Alert> : null}
                    <TextField select label="Empresa" value={form.id_empresa} onChange={(event) => setForm((atual) => ({...atual, id_empresa: event.target.value}))} disabled={referencias.carregando}>
                        {referencias.empresas.map((empresa) => <MenuItem key={empresa.id} value={empresa.id}>{empresa.nome_fantasia || empresa.razao_social}</MenuItem>)}
                    </TextField>
                    <TextField select label="Plano" value={form.id_plano} onChange={(event) => escolherPlano(event.target.value)} disabled={referencias.carregando}
                               helperText={versao ? `Versão ${versao.numero}, à venda: ${reais(versao.preco_base_mensal)} por mês${versao.dias_trial ? `, ${versao.dias_trial} dias de trial com cartão` : ''}.` : 'A assinatura nova entra na versão à venda do plano.'}>
                        {referencias.planos.map((item) => <MenuItem key={item.id} value={item.id}>{item.nome_plano} · {item.sistema?.nome}</MenuItem>)}
                    </TextField>
                    {meios.length ? (
                        <RadioGroup row value={form.meio_pagamento} onChange={(event) => setForm((atual) => ({...atual, meio_pagamento: event.target.value}))}>
                            {meios.map((meio) => <FormControlLabel key={meio} value={meio} control={<Radio size="small" />} label={getMeioLabel(meio)} />)}
                        </RadioGroup>
                    ) : null}
                    {form.meio_pagamento ? <Alert severity="info" icon={false}>{TEXTO_DA_SITUACAO[status]}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onFechar} disabled={salvando}>Cancelar</Button>
                <Button variant="contained" onClick={criar} disabled={salvando || !form.id_empresa || !versao || !form.meio_pagamento}>
                    {salvando ? 'Criando…' : 'Criar assinatura'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
